<template>
  <div class="modal panel" @click.self="close">
    <div ref="container" class="panel-container is-animated scale-up">
      <header class="panel-header">
        <div class="panel-left-item">
          <button class="text-button narrow-button" @click="close" v-t>Close</button>
        </div>
        <div v-if="showFormInHeader" class="panel-search">
          <form @submit.prevent="search">
            <button v-if="canGeolocate" type="button" class="icon-button" @click="geolocate" v-t:title="'My location'">
              <base-icon name="my_location" />
            </button>
            <input ref="input" type="search" :value="query" v-t:placeholder="'Search for a place'" />
            <button type="submit" class="icon-button" v-t:title="'Search'"><base-icon name="arrow_forward" /></button>
          </form>
        </div>
        <div v-else class="panel-title">{{ __('Place') }}</div>
        <div class="panel-right-item">
          <button class="flat-button narrow-button" @click="done" v-t>Save</button>
        </div>
      </header>
      <div class="panel-body">
        <div v-if="!showFormInHeader" class="panel-search">
          <form @submit.prevent="search">
            <button v-if="canGeolocate" type="button" class="icon-button" @click="geolocate" v-t:title="'My location'">
              <base-icon name="my_location" />
            </button>
            <input
              ref="input"
              type="search"
              :value="query"
              maxlength="1024"
              autofocus
              v-t:placeholder="'Search for a place'"
            />
            <button type="submit" class="icon-button" v-t:title="'Search'"><base-icon name="arrow_forward" /></button>
          </form>
        </div>
        <progress-indeterminate
          v-if="loading"
          class="map-browser-progress"
          :style="{ top: showFormInHeader ? '0' : '44px' }"
        />
        <img :src="imageSrc" :width="imageWidth" :height="imageHeight" @load="onImageLoad" @error="onImageError" />
      </div>
      <footer class="panel-footer">
        <div class="radio-button">
          <label
            ><input type="radio" :checked="mapType == 'roadmap'" value="roadmap" @change="changeMapType" /><span v-t
              >Road</span
            ></label
          >
          <label
            ><input type="radio" :checked="mapType == 'satellite'" value="satellite" @change="changeMapType" /><span v-t
              >Satellite</span
            ></label
          >
          <label
            ><input type="radio" :checked="mapType == 'terrain'" value="terrain" @change="changeMapType" /><span v-t
              >Terrain</span
            ></label
          >
          <label
            ><input type="radio" :checked="mapType == 'hybrid'" value="hybrid" @change="changeMapType" /><span v-t
              >Hybrid</span
            ></label
          >
        </div>
        <label class="map-browser-mark-check"
          ><check-box v-model="showMarker" /><span>{{ __('Show pin') }}</span></label
        >
      </footer>
    </div>

    <LocationPermissionAlertDialog
      :dir="dir"
      :is-windows="isWindows"
      :is-open="dialogVisible"
      @cancel="isOpen = false"
    />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters } from 'vuex'
import { processedUrl } from '@padlet/vivaldi-client'
import sample from 'lodash.sample'
import IntlMixin from 'vuemixins/intl_mixin'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'
import BaseIcon from 'vuecomponents/base_icon'
import ProgressIndeterminate from 'vuecomponents/progress_indeterminate'
import CheckBox from 'vuecomponents/check_box'
import LocationPermissionAlertDialog from 'vuecomponents/LocationPermissionAlertDialog'
import { lang } from 'bits/current_locale'
import { trackEvent } from 'bits/analytics'
import localStorage from 'bits/safe_storage'
import { captureException } from 'bits/error_tracker'

const RANDOM_QUERIES = [
  'leaning tower of pisa',
  'eiffel tower',
  'pyramids of giza',
  'taj mahal',
  'sydney opera house',
  'mt fuji',
  'mt everest',
  'roman colosseum',
  'word island dubai',
  'yellowstone spring',
  'victoria falls',
  'caspian sea',
  'pentagon',
  'potash ponds utah',
  'ayers rock',
  'dead sea',
  'capitol building',
  'easter island',
  'salar de uyuni',
  'red sea',
  'hoover dam',
  'matterhorn',
  'galesnjak island',
  'bernabeu',
  'london eye',
  'palm jumeirah',
  'statue of liberty',
  'mt rushmore',
  'pantheon',
  'shwedagon pagoda',
  'the kaaba',
  'st peters basilica',
  '51.848637, -0.55462', // lion point
  '51.562629, -1.605534', // jellyfish crop circle
]

const isCoordinates = (str) => {
  const strSplit = str.split(',')
  if (strSplit.length != 2) {
    return false
  }
  return !isNaN(strSplit[0]) && !isNaN(strSplit[1])
}

const extractPlaceFromGoogleMapsUrl = (str) => {
  const regex1 = /\!2s(.+?)\!/
  let matches = str.match(regex1)
  if (matches) {
    return matches[1]
  }

  const regex2 = /https?\:\/\/(.+?)\/place\/(.+?)\//
  matches = str.match(regex2)
  if (matches) {
    return matches[2]
  }

  return null
}

export default {
  components: { BaseIcon, ProgressIndeterminate, CheckBox, LocationPermissionAlertDialog },
  mixins: [IntlMixin, EscapeToCancelMixin],
  data() {
    return {
      canGeolocate: false,
      query: '',
      randomQuery: sample(RANDOM_QUERIES),
      loading: true,
      mapType: localStorage.getItem('mapBrowserMapType') || 'satellite',
      mapWidth: 640,
      mapHeight: 480,
      imageWidth: 640,
      imageHeight: 480,
      showMarker: localStorage.getItem('mapBrowserMarker') === 'yes',
      showFormInHeader: true,
      isOpen: false,
    }
  },
  computed: {
    ...mapGetters(['dir']),
    ...mapState({
      currentCountryCode: (state) => state.constants.currentCountryCode,
      isWindows: (state) => state.isWindows,
      isElectronApp: (state) => state.isElectronApp,
    }),
    ...mapState('browser', ['darkMode']),
    imageSrc() {
      if (this.query) {
        return this.mapSrc
      }
      return processedUrl(this.mapSrc, { width: this.imageWidth, height: this.imageHeight })
    },
    mapSrc() {
      let mapQuery = this.query ? this.query : this.randomQuery
      if (isCoordinates(mapQuery)) {
        mapQuery = `"${mapQuery}"`
      }
      mapQuery = encodeURIComponent(mapQuery)
      let src = `https://maps.googleapis.com/maps/api/staticmap?center=${mapQuery}&key=AIzaSyD3kjetwpeAYF-eXThlRhc1F1EYwsQvHcY&scale=2&size=${this.mapWidth}x${this.mapHeight}&maptype=${this.mapType}&language=${lang}&region=${this.currentCountryCode}`
      if (this.showMarker) {
        src = `${src}&markers=${mapQuery}`
      }
      return src
    },
    dialogVisible() {
      return this.isOpen && this.isElectronApp
    },
  },
  watch: {
    mapType() {
      this.loading = true
    },
    query() {
      this.loading = true
    },
    showMarker(newValue) {
      this.loading = true
      localStorage.setItem('mapBrowserMarker', newValue ? 'yes' : 'no')
      trackEvent('Maps Browser', 'Changed Marker', newValue)
    },
  },
  mounted() {
    trackEvent('Maps Browser', 'Mounted', this.mapType)
    this.canGeolocate = 'geolocation' in navigator
    this.imageWidth = this.$refs.container.clientWidth
    this.showFormInHeader = this.imageWidth >= 640
    this.imageHeight = Math.floor(this.imageWidth * 0.75)
    this.$refs.input.focus()
  },
  methods: {
    changeMapType(e) {
      const newMapType = e.target.value
      trackEvent('Maps Browser', 'Changed Type', newMapType)
      localStorage.setItem('mapBrowserMapType', newMapType)
      this.mapType = newMapType
    },
    cancel() {
      this.close()
    },
    close() {
      this.$emit('close')
    },
    done() {
      this.$emit('select', this.mapSrc)
    },
    geolocate() {
      trackEvent('Maps Browser', 'Geolocation Started')
      this.loading = true
      navigator.geolocation.getCurrentPosition(this.onLocationSuccess.bind(this), this.onLocationError.bind(this))

      // The dialog will be shown if the app doesn't have the location permission
      // and after 3 seconds since the time the app calls `navigator.geolocation.getCurrentPosition`
      setTimeout(() => {
        try {
          const hasLocationPermission = JSON.parse(localStorage.getItem('hasLocationPermission'))
          if (!hasLocationPermission) {
            this.isOpen = true
          }
        } catch (error) {
          captureException(error)
        }
      }, 3000)
    },
    onImageLoad() {
      this.loading = false
    },
    onImageError() {
      this.loading = false
    },
    onLocationSuccess(position) {
      trackEvent('Maps Browser', 'Geolocation Completed')
      this.loading = false
      this.query = [position.coords.latitude, position.coords.longitude].join(',')
      localStorage.setItem('hasLocationPermission', true)
    },
    onLocationError(error) {
      trackEvent('Maps Browser', 'Geolocation Failed')
      this.loading = false
      if (error.code === error.PERMISSION_DENIED) {
        localStorage.setItem('hasLocationPermission', false)
        alert(
          this.__(
            "Looks like we don't have permission to see your location. You may want to change that. (Don't worry, we do not store your location on our servers.)",
          ),
        )
      }
    },
    search() {
      const inputValue = this.$refs.input.value
      // People are pasting Google Maps URLs
      const extractedQuery = extractPlaceFromGoogleMapsUrl(inputValue)
      this.query = extractedQuery || inputValue
      if (!isCoordinates(this.query)) {
        trackEvent('Maps Browser', 'Searched', this.query)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.panel {
  .panel-left-item,
  .panel-right-item {
    min-width: unset;
  }
}
.panel-search {
  flex-grow: 1;
}
img {
  width: 100%;
  background-color: $default-background-color;
}
form {
  background-color: white;
  flex-shrink: 0;
  padding: 0px 4px 0 8px;
  margin: 0 8px;

  display: flex;
  border-radius: $default-border-radius;
  background: $grey-2;

  button {
    padding: 4px;
  }

  button[type='button'] i {
    font-size: 16px;
  }

  button[type='submit'] {
    visibility: hidden;
  }

  input {
    box-shadow: none;
    margin-bottom: 0;
    line-height: 20px;
    padding: 6px 8px;
    box-sizing: border-box;

    &:focus,
    &:active {
      box-shadow: none;
    }
  }

  input:focus,
  input:active,
  input:not(:empty) {
    & + button {
      visibility: visible;
    }
  }
}
.panel-body {
  position: relative;
  flex-grow: 0;
  form {
    margin: 4px 8px 8px;
  }
}
.map-browser-progress {
  position: absolute;
  width: 100%;
  left: 0;
}
.map-browser-mark-check {
  display: flex;
  align-items: center;
  height: 32px;

  span:last-child {
    margin: 0px 4px;
    font-size: 14px;
  }
}
@include respondToPhone {
  .panel-container {
    .panel-body {
      flex-grow: 0;
    }

    footer {
      .radio-button {
        margin: 8px 0;
      }
    }
  }
}
</style>
